// Organization Users Tab - Lists all admin users in the organization
import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { OrganizationUsersTabSkeleton } from './OrganizationUsersTabSkeleton';
import { GlassCard } from '@/components/shared/redesign';
import { SVG_COMPONENTS } from '@/assets/admin';

interface Admin {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface OrganizationUsersTabProps {
  organizationId: number;
  admins: Admin[];
  isLoading: boolean;
}

export const OrganizationUsersTab: React.FC<OrganizationUsersTabProps> = ({
  organizationId,
  admins,
  isLoading,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();

  if (isLoading) {
    return <OrganizationUsersTabSkeleton />;
  }

  if (!admins || admins.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <SVG_COMPONENTS.ADMIN width={48} height={48} />
        <Text style={styles.emptyTitle}>No Users</Text>
        <Text style={styles.emptyMessage}>
          This organization doesn't have any admin users assigned yet.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Admin Users</Text>
        <Text style={styles.sectionSubtitle}>
          {admins.length} {admins.length === 1 ? 'user' : 'users'} with access to this organization
        </Text>

        <View style={styles.listContainer}>
          {admins.map((adminRow) => (
            <GlassCard key={adminRow.id} padding={16}>
              <View style={styles.userItem}>
                <View style={styles.userIconContainer}>
                  <SVG_COMPONENTS.ADMIN width={20} height={20} color={admin.dataCell.textMuted} />
                </View>
                <View style={styles.userInfo}>
                  <Text style={styles.userName}>
                    {adminRow.firstName} {adminRow.lastName}
                  </Text>
                  <Text style={styles.userEmail}>{adminRow.email}</Text>
                </View>
              </View>
            </GlassCard>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      container: {
        flex: 1,
        paddingHorizontal: 20,
      },
      section: {
        paddingVertical: 20,
      },
      sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 4,
      },
      sectionSubtitle: {
        fontSize: 13,
        color: components.admin.dataCell.textMuted,
        marginBottom: 20,
      },
      listContainer: {
        gap: 8,
      },
      userItem: {
        flexDirection: 'row',
        alignItems: 'center',
      },
      userIconContainer: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: components.admin.formInput.bg,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
      },
      userInfo: {
        flex: 1,
      },
      userName: {
        fontSize: 14,
        fontWeight: '500',
        color: colors.text.primary,
        marginBottom: 2,
      },
      userEmail: {
        fontSize: 12,
        color: components.admin.dataCell.textMuted,
      },
      loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
      },
      loadingText: {
        marginTop: 12,
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
      },
      emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
      },
      emptyTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text.primary,
        marginTop: 16,
        marginBottom: 8,
      },
      emptyMessage: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
        textAlign: 'center',
      },
    }),
  );
